import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import AddEmployees from "./addEmployees";
import UploadEmployeesCsvFile from "./uploadEmployeesCsvFile";
import EmployeesCsvFormat from "./employeeCsvFormat";
import ArchiveEmployeeModal from "./archiveEmployeePopup";
import "./hm-theme.css";

function EmployeesDetails({ selectedOrgcode }) {
  const [employeesDetails, setEmployeeDetails] = useState([]);
  const [loading, setLoading] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCsvUplaodModalOpen, setIsCsvUplaodModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [company, setCompany] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [employeeDetailsID, setEmployeeDetsilsId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const token = JSON.parse(localStorage.getItem("data")).token;
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm.trim()) {
      searchData(0);
    } else {
      getData(currentPage);
    }
  }, [searchTerm]);

  const searchData = async (pageNumber = 0) => {
    setLoading(true);
    try {
      const searchUrl = `/api/employeedetails/searchByNameOrEmail`;
      const response = await axios.post(searchUrl, { searchTerm, companyId: company?.id }, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployeeDetails(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoading(true);
    try {
      const compRes = await axios.get(`/api/companydetails/companycode=${selectedOrgcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(compRes.data);
      const trans = await axios.get(`/api/employeedetails/companyId=${compRes.data?.id}`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setEmployeeDetails(trans.data.content || []);
      setCurrentPage(trans.data.number || 0);
      setTotalPages(trans.data.totalPages || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  function onClickhandler(e, id, disabled) {
    setLoading(true);
    axios.post(`/api/employeedetails/update`, { id, disabled }, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => {
      setLoading(false);
      toast.success("Employee status updated successfully");
      getData(currentPage);
    }).catch((error) => {
      setLoading(false);
      handleError(error);
    });
  }

  function onClickhandlerAnnualCheckup(e, id, annualCheckup) {
    e.preventDefault();
    setLoading(true);
    axios.post(`/api/employeedetails/update`, { id, annualCheckup }, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => {
      setLoading(false);
      toast.success("Annual checkup status updated successfully");
      getData(currentPage);
    }).catch((error) => {
      setLoading(false);
      handleError(error);
    });
  }

  const openUserProfileModal = (userid) => {
    navigate(`/users/employee-profile/${userid}`);
  };

  const handleDelete = async (id) => {
    const options = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setLoading(true);
      const response = await axios.get(`/api/employeedetails/checkForDeletion/id=${id}`, options);
      if (response.data === false) {
        setShowModal(true);
        setEmployeeDetsilsId(id);
      } else {
        setEmployeeDetsilsId(id);
        setShowConfirmationModal(true);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const options = { headers: { Authorization: `Bearer ${token}` } };
    try {
      setLoading(true);
      await axios.delete(`/api/employeedetails`, { data: { id: employeeDetailsID }, ...options });
      toast.success('Employee deleted successfully!');
      setShowConfirmationModal(false);
      setEmployeeDetsilsId(null);
      getData(currentPage);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const bulkUpdate = (companycode) => {
    navigate(`/users/bulk-update/${companycode}`);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  return (
    <div>
      {loading && <div className="loading"></div>}

      <div className="hm-page-wrapper">
        {/* Page Header */}
        <div className="hm-page-header mb-4">
          <div>
            <h2 className="hm-page-title">
              <i className="material-icons">badge</i>
              Employee Roster — {company?.companyname || selectedOrgcode}
            </h2>
            <p className="hm-page-subtitle">Manage company employees, permissions, and annual checkups</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button className="btn btn-outline-secondary rounded-pill px-3" onClick={() => bulkUpdate(selectedOrgcode)}>
              Bulk Update
            </button>
            <button className="btn btn-outline-primary rounded-pill px-3" onClick={() => setShowImageModal(true)}>
              Format Guide
            </button>
            <button className="btn btn-outline-secondary rounded-pill px-3" onClick={() => setIsCsvUplaodModalOpen(true)}>
              Upload CSV
            </button>
            <button className="btn-hm-primary" onClick={() => setIsModalOpen(true)}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>add</i>
              Add Employee
            </button>
          </div>
        </div>

        {/* Filter Card */}
        <div className="hm-card mb-4">
          <div className="hm-card-header">
            <h3 className="hm-card-title">
              <i className="material-icons">search</i>
              Search Employees
            </h3>
          </div>
          <div className="hm-card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control ps-4"
                    placeholder="Search by employee name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                    search
                  </i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Employee Cards Grid */}
        <div className="hm-card mb-4">
          <div className="hm-card-header">
            <h3 className="hm-card-title">
              <i className="material-icons">groups</i>
              Employees Roster
            </h3>
            <span className="hm-badge hm-badge-primary">{employeesDetails.length} employees</span>
          </div>
          <div className="hm-card-body">
            {employeesDetails.length === 0 ? (
              <div className="hm-empty-state">
                <i className="material-icons">person_off</i>
                <p>No employees found for this company</p>
              </div>
            ) : (
              <div className="row g-4">
                {employeesDetails.map((accd) => (
                  <div className="col-md-6 col-lg-4" key={accd.id}>
                    <div
                      className="hm-card h-100 p-3 d-flex flex-column justify-content-between"
                      style={{ cursor: "pointer" }}
                      onClick={() => openUserProfileModal(accd.userid)}
                    >
                      {/* Top Controls: Activity Switch & Delete Icon */}
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" onClick={(e) => e.stopPropagation()}>
                        <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={accd.disabled === false}
                            onChange={(e) => onClickhandler(e, accd.id, !accd.disabled)}
                            title="Toggle active status"
                            style={{ cursor: "pointer" }}
                          />
                          <span className="small fw-semibold text-muted">
                            {accd.disabled ? "Inactive" : "Active"}
                          </span>
                        </div>

                        <button
                          className="btn btn-link text-danger p-0 border-0"
                          onClick={() => handleDelete(accd.id)}
                          title="Delete Employee"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>

                      {/* Profile Header */}
                      <div className="text-center my-2">
                        {accd.employeeProfileImage?.image ? (
                          <img
                            src={`${accd.employeeProfileImage?.imageMeta},${accd.employeeProfileImage?.image}`}
                            alt="Avatar"
                            className="rounded-circle mb-2"
                            style={{ width: "80px", height: "80px", objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2"
                            style={{ width: "80px", height: "80px", background: "rgba(47, 58, 136, 0.1)", color: "#2f3a88", fontSize: "2.2rem" }}
                          >
                            <i className="material-icons">person</i>
                          </div>
                        )}
                        <h5 className="fw-bold mb-1">{accd.name} {accd.lastname}</h5>
                        {accd.designation && (
                          <span className="hm-badge hm-badge-primary mb-1">{accd.designation}</span>
                        )}
                      </div>

                      {/* Employee Details */}
                      <div className="small text-muted mb-3 border-top pt-2">
                        <div className="mb-1"><i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>badge</i>ID: {accd.empid || "N/A"}</div>
                        <div className="mb-1"><i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>email</i>{accd.email}</div>
                        <div><i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>phone</i>{accd.phoneno || "N/A"}</div>
                      </div>

                      {/* Bottom Controls: Annual Checkup Status */}
                      <div className="pt-2 border-top d-flex justify-content-between align-items-center" onClick={(e) => e.stopPropagation()}>
                        <span className="small fw-semibold text-muted">Annual Checkup</span>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={accd.annualCheckup === false}
                            onChange={(e) => onClickhandlerAnnualCheckup(e, accd.id, !accd.annualCheckup)}
                            title={accd.annualCheckup === false ? "Annual checkup enabled" : "Annual checkup disabled"}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination className="hm-pagination">
                  <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                  {renderPageNumbers()}
                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                  <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                </Pagination>
              </div>
            )}
          </div>
        </div>

      </div>

      {isModalOpen && (
        <AddEmployees isOpen={isModalOpen} onRequestClose={() => { setIsModalOpen(false); getData(currentPage); }} companyId={company?.id} />
      )}
      {isCsvUplaodModalOpen && (
        <UploadEmployeesCsvFile isOpen={isCsvUplaodModalOpen} onRequestClose={() => { setIsCsvUplaodModalOpen(false); getData(currentPage); }} companyId={company?.id} companycode={company?.companycode} />
      )}
      {showImageModal && (
        <EmployeesCsvFormat isOpen={showImageModal} onRequestClose={() => setShowImageModal(false)} />
      )}
      {showModal && (
        <ArchiveEmployeeModal
          isOpen={showModal}
          onRequestClose={() => { setShowModal(false); setEmployeeDetsilsId(null); getData(currentPage); }}
          employeeId={employeeDetailsID}
        />
      )}

      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this employee? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="me-3" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EmployeesDetails;